import type { ReactNode } from 'react';
import type { ClientAddress, ClientFact } from '../types';
import { Card, MicroLabel, SectionHead, cn } from '../ui/primitives';

/**
 * The company record and where things go: facts as a grid, addresses as cards.
 *
 * ── Why the facts are a grid and not a form ──
 *
 * This section is read first and edited rarely. A column of labelled inputs
 * makes every visit look like a form waiting to be filled in; a dense grid of
 * label-over-value cells is scannable, and editing is one action away. The v1
 * page had it the other way round and the tab opened looking like paperwork.
 *
 * Identifiers — VAT, customer numbers, phone — are set `mono`, because the
 * thing people actually do with them is compare one against another, and
 * proportional digits make that harder than it needs to be.
 *
 * ── Why addresses are cards ──
 *
 * An address is four to five lines that must not wrap unpredictably, and a
 * client has several with different jobs (bill here, ship there, this one is
 * only for the spa). Rows would either truncate the lines or give each address
 * a five-line row; cards let the whole address be read at once and carry the
 * one thing a list cannot — which brand's deliveries it is for.
 */
export function ClientDetails({
	facts,
	addresses,
	meta,
	action,
	renderAddressAction,
	renderAddressIcon,
	addAddressSlot,
}: {
	facts: ClientFact[];
	addresses: ClientAddress[];
	meta?: ReactNode;
	/** "Edit details" — the app's, because it owns the form and the mutation. */
	action?: ReactNode;
	/** The per-card menu — edit, make default, remove. */
	renderAddressAction?: (address: ClientAddress) => ReactNode;
	/** The glyph beside the kind — the app's, since only it knows a shipping
	 * address from a billing one. */
	renderAddressIcon?: (address: ClientAddress) => ReactNode;
	/**
	 * The dashed "add address" tile that closes the grid.
	 *
	 * It sits IN the grid rather than beside it so the affordance is where the
	 * eye already is — after the last address — and so a client with none still
	 * shows the grid's shape instead of collapsing to a heading.
	 */
	addAddressSlot?: ReactNode;
}) {
	return (
		<>
			<SectionHead title="Company & addresses" meta={meta} action={action} />

			<div className="flex flex-col gap-3.5">
				{facts.length > 0 && (
					<Card>
						<div className="grid grid-cols-[repeat(auto-fit,minmax(12.5rem,1fr))] gap-px bg-line-1">
							{facts.map((fact) => (
								<div key={fact.label} className="min-w-0 bg-surface px-4.5 py-3">
									<MicroLabel className="mb-1 block">{fact.label}</MicroLabel>
									<div
										className={cn(
											'truncate text-xs text-text-1',
											fact.mono && 'font-mono'
										)}
										title={fact.value ?? undefined}
									>
										{fact.value || <span className="text-text-4">—</span>}
									</div>
								</div>
							))}
						</div>
					</Card>
				)}

				<div className="grid grid-cols-[repeat(auto-fill,minmax(13.75rem,1fr))] gap-3.5">
					{addresses.map((address) => (
						<Card key={address.id} padded className="flex flex-col">
							<div className="mb-2.5 flex items-center gap-2">
								{renderAddressIcon && (
									<span className="flex shrink-0 text-text-4">
										{renderAddressIcon(address)}
									</span>
								)}
								<MicroLabel
									className={address.emphasis ? 'text-text-2' : undefined}
								>
									{address.kind}
								</MicroLabel>
								{address.isPrimary && (
									<span className="text-2xs text-text-4">· primary</span>
								)}
								{renderAddressAction && (
									<span className="ml-auto shrink-0">
										{renderAddressAction(address)}
									</span>
								)}
							</div>

							{address.label && (
								<div className="mb-1 truncate text-xs font-medium text-text-1">
									{address.label}
								</div>
							)}

							<div className="text-2xs leading-relaxed text-text-3">
								{address.lines.map((line, i) => (
									<div key={`${line}-${i}`}>{line}</div>
								))}
							</div>

							{address.brand && (
								<div className="mt-2.5 flex items-center gap-1.5 border-t border-line-1 pt-2.5 text-2xs text-text-4">
									<span
										className="h-2 w-2 shrink-0 rounded-[2px]"
										style={{
											background: address.brand.color ?? 'var(--line-2)',
										}}
									/>
									Used for {address.brand.name}
								</div>
							)}
						</Card>
					))}

					{addAddressSlot}
				</div>
			</div>
		</>
	);
}
